import { Box } from "@mui/material";
import { useContext, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { AuthContext } from "../../../context/AuthContext";
import {
  useSummaryTemplates,
  useCreateSummaryTemplate,
  useUpdateSummaryTemplate,
  useDeleteSummaryTemplate,
  useSetDefaultTemplate,
  useHideSystemTemplate,
  useUnhideSystemTemplate,

} from "../../../hooks/useSummaryTemplates";
import type {
  SummaryTemplate,
  OutputFormat,
  LengthGuidance,
} from "../../../types/summaryTemplate/SummaryTemplate";
import {
  toolbarBtnSx,
  flyoutInputSx,
  flyoutLabelSx,
  flyoutSelectSx,
  flyoutTextareaSx,
  crudTableSx,
  CrudPageLayout,
  LoadingState,
  EmptyState,
  FlyoutDrawer,
  FlyoutFooter,
  DeleteConfirmation,
  PlusIcon,
  formErrorSx,
} from "../../components/shared";

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const MAX_SECTIONS = 10;

// ---------------------------------------------------------------------------
// Template type helpers
// ---------------------------------------------------------------------------

type TemplateType = "system" | "customer" | "personal";

function templateType(t: SummaryTemplate): TemplateType {
  if (t.customerId === "system") return "system";
  if (t.userId) return "personal";
  return "customer";
}

const TYPE_ORDER: Record<TemplateType, number> = {
  system: 0,
  customer: 1,
  personal: 2,
};

const TYPE_COLORS: Record<TemplateType, string> = {
  system: "#1976d2",
  customer: "#388e3c",
  personal: "#f57c00",
};

const TYPE_BG: Record<TemplateType, string> = {
  system: "rgba(25,118,210,0.12)",
  customer: "rgba(56,142,60,0.12)",
  personal: "rgba(245,124,0,0.12)",
};

function formatLabel(format: OutputFormat, t: (key: string) => string): string {
  switch (format) {
    case "BulletPoints": return t("summaryTemplate:bulletPoints");
    case "NumberedList": return t("summaryTemplate:numberedList");
    case "Prose": return t("summaryTemplate:prose");
    default: return format;
  }
}



// ---------------------------------------------------------------------------
// ActionBtn — small inline table row button
// ---------------------------------------------------------------------------

function ActionBtn({
  onClick,
  danger = false,
  children,
}: {
  readonly onClick: () => void;
  readonly danger?: boolean;
  readonly children: React.ReactNode;
}) {
  return (
    <Box
      component="button"
      onClick={(e: React.MouseEvent) => {
        e.stopPropagation();
        onClick();
      }}
      sx={{
        display: "inline-flex",
        alignItems: "center",
        gap: "4px",
        px: "8px",
        py: "4px",
        borderRadius: "5px",
        border: danger ? "1px solid rgba(251,113,133,0.4)" : "1px solid var(--border-mid)",
        bgcolor: danger ? "rgba(251,113,133,0.08)" : "var(--bg-raised)",
        color: danger ? "var(--signal-rose)" : "var(--text-secondary)",
        fontSize: "0.75rem",
        fontWeight: 600,
        fontFamily: "inherit",
        cursor: "pointer",
        "&:hover": danger
          ? { bgcolor: "rgba(251,113,133,0.18)", color: "#FCA5A5" }
          : { bgcolor: "var(--bg-hover)", color: "var(--text-primary)" },
      }}
    >
      {children}
    </Box>
  );
}

// ---------------------------------------------------------------------------
// Section form item interface
// ---------------------------------------------------------------------------

let _sectionUidCounter = 0;
function newSectionUid(): string {
  _sectionUidCounter += 1;
  return `section-${Date.now()}-${_sectionUidCounter}`;
}

interface SectionFormItem {
  uid: string;
  heading: string;
  instructions: string;
}

// ---------------------------------------------------------------------------
// Main Component
// ---------------------------------------------------------------------------

export default function V2SummaryTemplates() {
  const { t } = useTranslation(["summaryTemplate", "common"]);
  const { customerId } = useParams<{ customerId: string }>();
  const { user: authUser } = useContext(AuthContext);

  // -- Data ------------------------------------------------------------------
  const { data: templateData, isLoading } = useSummaryTemplates(customerId);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const templates: SummaryTemplate[] = templateData?.templates ?? [];
  const hiddenSystemTemplateIds: string[] = templateData?.hiddenSystemTemplateIds ?? [];

  const sortedTemplates = useMemo(() => {
    return [...templates].sort((a, b) => {
      const ta = templateType(a);
      const tb = templateType(b);
      if (TYPE_ORDER[ta] !== TYPE_ORDER[tb]) return TYPE_ORDER[ta] - TYPE_ORDER[tb];
      return a.name.localeCompare(b.name);
    });
  }, [templates]);

  // -- Permissions -----------------------------------------------------------
  const canManage = authUser?.hasPermission("summary-templates:Manage") ?? false;

  // -- Mutations -------------------------------------------------------------
  const createMutation = useCreateSummaryTemplate(customerId);
  const updateMutation = useUpdateSummaryTemplate(customerId);
  const deleteMutation = useDeleteSummaryTemplate(customerId);
  const setDefaultMutation = useSetDefaultTemplate(customerId);
  const hideMutation = useHideSystemTemplate(customerId);
  const unhideMutation = useUnhideSystemTemplate(customerId);


  // -- Flyout state ----------------------------------------------------------
  const [flyoutOpen, setFlyoutOpen] = useState(false);
  const [flyoutMode, setFlyoutMode] = useState<"create" | "edit">("create");
  const [editingTemplateId, setEditingTemplateId] = useState<string | null>(null);

  // -- Form state ------------------------------------------------------------
  const [formName, setFormName] = useState("");
  const [formScope, setFormScope] = useState<"customer" | "personal">("customer");
  const [formOutputFormat, setFormOutputFormat] = useState<OutputFormat>("BulletPoints");
  const [formLengthGuidance, setFormLengthGuidance] = useState<LengthGuidance>("Standard");
  const [formGlobalInstructions, setFormGlobalInstructions] = useState("");
  const [formSections, setFormSections] = useState<SectionFormItem[]>([]);
  const [formError, setFormError] = useState("");

  // -- Delete confirmation state ---------------------------------------------
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deleteError, setDeleteError] = useState("");

  // -- Helpers ---------------------------------------------------------------
  const resetForm = () => {
    setFormName("");
    setFormScope("customer");
    setFormOutputFormat("BulletPoints");
    setFormLengthGuidance("Standard");
    setFormGlobalInstructions("");
    setFormSections([]);
    setFormError("");
    setShowDeleteConfirm(false);
    setDeleteError("");
  };

  const populateFromTemplate = (tmpl: SummaryTemplate) => {
    setFormName(tmpl.name);
    setFormOutputFormat(tmpl.outputFormat);
    setFormLengthGuidance(tmpl.lengthGuidance);
    setFormGlobalInstructions(tmpl.globalInstructions ?? "");
    setFormSections(
      [...tmpl.sections]
        .sort((a, b) => a.order - b.order)
        .map((s) => ({ uid: newSectionUid(), heading: s.heading, instructions: s.instructions })),
    );
    setFormError("");
    setShowDeleteConfirm(false);
    setDeleteError("");
  };

  const handleCreate = () => {
    resetForm();
    setFlyoutMode("create");
    setEditingTemplateId(null);
    setFlyoutOpen(true);
  };

  const handleEdit = (tmpl: SummaryTemplate) => {
    populateFromTemplate(tmpl);
    setFlyoutMode("edit");
    setEditingTemplateId(tmpl.id);
    setFlyoutOpen(true);
  };

  const handleClone = (tmpl: SummaryTemplate) => {
    populateFromTemplate(tmpl);
    setFormName(`Copy of ${tmpl.name}`);
    setFormScope("personal");
    setFlyoutMode("create");
    setEditingTemplateId(null);
    setFlyoutOpen(true);
  };

  const handleCloseFlyout = () => {
    setFlyoutOpen(false);
    resetForm();
    setEditingTemplateId(null);
  };

  const handleDeleteDirect = (tmpl: SummaryTemplate) => {
    populateFromTemplate(tmpl);
    setFlyoutMode("edit");
    setEditingTemplateId(tmpl.id);
    setShowDeleteConfirm(true);
    setFlyoutOpen(true);
  };

  const handleSetDefault = (tmpl: SummaryTemplate) => {
    setDefaultMutation.mutate(tmpl.id);
  };

  const handleHide = (tmpl: SummaryTemplate) => {
    hideMutation.mutate(tmpl.id);
  };

  const handleUnhide = (tmpl: SummaryTemplate) => {
    unhideMutation.mutate(tmpl.id);
  };

  const handleSave = () => {
    if (!formName.trim()) {
      setFormError(t("summaryTemplate:templateName") + " is required.");
      return;
    }
    if (formSections.some((s) => !s.heading.trim() || !s.instructions.trim())) {
      setFormError("All sections must have a heading and instructions.");
      return;
    }
    setFormError("");

    const sectionsPayload = formSections.map((s, i) => ({
      heading: s.heading.trim(),
      instructions: s.instructions.trim(),
      order: i,
    }));

    if (flyoutMode === "create") {
      createMutation.mutate(
        {
          name: formName.trim(),
          scope: formScope,
          outputFormat: formOutputFormat,
          lengthGuidance: formLengthGuidance,
          globalInstructions: formGlobalInstructions.trim() || null,
          sections: sectionsPayload,
        },
        {
          onSuccess: () => {
            handleCloseFlyout();
          },
          onError: (err) => setFormError(err.message || "Failed to create template."),
        },
      );
    } else if (editingTemplateId) {
      updateMutation.mutate(
        {
          templateId: editingTemplateId,
          request: {
            name: formName.trim(),
            outputFormat: formOutputFormat,
            lengthGuidance: formLengthGuidance,
            globalInstructions: formGlobalInstructions.trim() || null,
            sections: sectionsPayload,
          },
        },
        {
          onSuccess: () => {
            handleCloseFlyout();
          },
          onError: (err) => setFormError(err.message || "Failed to update template."),
        },
      );
    }
  };

  const handleDelete = () => {
    if (!editingTemplateId) return;
    setDeleteError("");
    deleteMutation.mutate(editingTemplateId, {
      onSuccess: () => {
        handleCloseFlyout();
      },
      onError: (err) => setDeleteError(err.message || "Failed to delete template."),
    });
  };

  // -- Section helpers -------------------------------------------------------
  const addSection = () => {
    if (formSections.length >= MAX_SECTIONS) return;
    setFormSections((prev) => [...prev, { uid: newSectionUid(), heading: "", instructions: "" }]);
  };

  const removeSection = (index: number) => {
    setFormSections((prev) => prev.filter((_, i) => i !== index));
  };

  const updateSection = (index: number, field: keyof SectionFormItem, value: string) => {
    setFormSections((prev) =>
      prev.map((s, i) => (i === index ? { ...s, [field]: value } : s)),
    );
  };

  // -- Per-template permission helpers ---------------------------------------
  const canEditTemplate = (tmpl: SummaryTemplate): boolean => {
    const type = templateType(tmpl);
    if (type === "system") return false;
    if (type === "personal") return tmpl.userId === authUser?.userId;
    return canManage;
  };

  const canDeleteTemplate = canEditTemplate;

  const canSetDefault = (tmpl: SummaryTemplate): boolean => {
    const type = templateType(tmpl);
    if (type === "system") return false;
    return canManage || tmpl.userId === authUser?.userId;
  };

  const editingTemplate = editingTemplateId
    ? templates.find((t) => t.id === editingTemplateId) ?? null
    : null;

  const isSaving = createMutation.isPending || updateMutation.isPending;

  const toolbarContent = (
    <Box component="button" onClick={handleCreate} sx={toolbarBtnSx(false)}>
      <PlusIcon />
      {t("summaryTemplate:newTemplate")}
    </Box>
  );

  const tableContent = isLoading ? (
    <LoadingState message="Loading templates\u2026" />
  ) : sortedTemplates.length === 0 ? (
    <EmptyState message="No templates found." />
  ) : (
    <Box component="table" sx={crudTableSx}>
            <thead>
              <tr>
                <th>{t("summaryTemplate:templateName")}</th>
                <th>Type</th>
                <th>{t("summaryTemplate:outputFormat")}</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {sortedTemplates.map((tmpl) => {
                const type = templateType(tmpl);
                const isHidden = hiddenSystemTemplateIds.includes(tmpl.id);
                const showAsHidden = type === "system" && isHidden;

                return (
                  <Box
                    component="tr"
                    key={tmpl.id}
                    sx={{
                      opacity: showAsHidden ? 0.5 : 1,
                    }}
                  >
                    {/* Name */}
                    <Box
                      component="td"
                      sx={{
                        fontWeight: 600,
                        color: "var(--text-primary) !important",
                      }}
                    >
                      <Box sx={{ display: "flex", alignItems: "center", gap: "8px" }}>
                        <Box
                          component="span"
                          sx={{
                            textDecoration: showAsHidden ? "line-through" : "none",
                          }}
                        >
                          {tmpl.name}
                        </Box>
                        {tmpl.isDefault && (
                          <Box
                            component="span"
                            sx={{
                              px: "5px",
                              py: "1px",
                              borderRadius: "4px",
                              fontSize: "0.65rem",
                              fontWeight: 700,
                              letterSpacing: "0.06em",
                              backgroundColor: "rgba(99,102,241,0.15)",
                              color: "#818CF8",
                              border: "1px solid rgba(99,102,241,0.3)",
                            }}
                          >
                            {t("summaryTemplate:default")}
                          </Box>
                        )}
                      </Box>
                    </Box>

                    {/* Type */}
                    <td>
                      <Box
                        component="span"
                        sx={{
                          px: "7px",
                          py: "2px",
                          borderRadius: "4px",
                          fontSize: "0.72rem",
                          fontWeight: 600,
                          backgroundColor: TYPE_BG[type],
                          color: TYPE_COLORS[type],
                          textTransform: "capitalize",
                        }}
                      >
                        {type}
                      </Box>
                    </td>

                    {/* Format */}
                    <td>{formatLabel(tmpl.outputFormat, t)} · {tmpl.lengthGuidance}</td>

                    {/* Actions */}
                    <td>
                      <Box sx={{ display: "flex", alignItems: "center", gap: "6px" }}>
                        {/* System template actions */}
                        {type === "system" && (
                          <>
                            <ActionBtn onClick={() => handleClone(tmpl)}>
                              {t("summaryTemplate:clone")}
                            </ActionBtn>
                            {canManage && !isHidden && (
                              <ActionBtn onClick={() => handleHide(tmpl)}>
                                {t("summaryTemplate:hide")}
                              </ActionBtn>
                            )}
                            {canManage && isHidden && (
                              <ActionBtn onClick={() => handleUnhide(tmpl)}>
                                {t("summaryTemplate:unhide")}
                              </ActionBtn>
                            )}
                            {canManage && !isHidden && !tmpl.isDefault && (
                              <ActionBtn onClick={() => handleSetDefault(tmpl)}>
                                {t("summaryTemplate:setDefault")}
                              </ActionBtn>
                            )}
                          </>
                        )}

                        {/* Customer / personal template actions */}
                        {type !== "system" && (
                          <>
                            {canEditTemplate(tmpl) && (
                              <ActionBtn onClick={() => handleEdit(tmpl)}>
                                {t("summaryTemplate:edit")}
                              </ActionBtn>
                            )}
                            {canSetDefault(tmpl) && !tmpl.isDefault && (
                              <ActionBtn onClick={() => handleSetDefault(tmpl)}>
                                {t("summaryTemplate:setDefault")}
                              </ActionBtn>
                            )}
                            {canDeleteTemplate(tmpl) && (
                              <ActionBtn danger onClick={() => handleDeleteDirect(tmpl)}>
                                {t("summaryTemplate:delete")}
                              </ActionBtn>
                            )}
                          </>
                        )}
                      </Box>
                    </td>
                  </Box>
                );
              })}
            </tbody>
          </Box>
  );

  const flyoutTitle = flyoutMode === "create" ? t("summaryTemplate:newTemplate") : t("summaryTemplate:edit");
  const saveLabel = (() => {
    const isCreate = flyoutMode === "create";
    if (isSaving) return isCreate ? "Creating\u2026" : "Saving\u2026";
    return isCreate ? t("summaryTemplate:newTemplate") : t("common:saveChanges");
  })();

  return (
    <CrudPageLayout
      toolbar={toolbarContent}
      extra={
        <FlyoutDrawer
          open={flyoutOpen}
          onClose={handleCloseFlyout}
          title={flyoutTitle}
          footer={
            <FlyoutFooter
              onCancel={handleCloseFlyout}
              cancelLabel={t("common:cancel")}
              onSave={handleSave}
              saveLabel={saveLabel}
              isSaving={isSaving}
              showDelete={flyoutMode === "edit" && !!editingTemplate && canDeleteTemplate(editingTemplate) && !showDeleteConfirm}
              onDelete={() => setShowDeleteConfirm(true)}
              deleteLabel={t("summaryTemplate:delete")}
            />
          }
        >
          {formError && (
            <Box sx={formErrorSx}>{formError}</Box>
          )}

            {/* Name */}
            <Box>
              <Box sx={flyoutLabelSx}>{t("summaryTemplate:templateName")}</Box>
              <Box
                component="input"
                type="text"
                placeholder={t("summaryTemplate:templateName")}
                value={formName}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => setFormName(e.target.value)}
                sx={flyoutInputSx}
              />
            </Box>

            {/* Scope (create only) */}
            {flyoutMode === "create" && (
              <Box>
                <Box sx={flyoutLabelSx}>{t("summaryTemplate:scope")}</Box>
                <Box
                  component="select"
                  value={formScope}
                  onChange={(e: React.ChangeEvent<HTMLSelectElement>) =>
                    setFormScope(e.target.value as "customer" | "personal")
                  }
                  sx={flyoutSelectSx}
                >
                  <option value="personal">{t("summaryTemplate:scopePersonal")}</option>
                  {canManage && (
                    <option value="customer">{t("summaryTemplate:scopeCustomer")}</option>
                  )}
                </Box>
              </Box>
            )}

            {/* Output Format */}
            <Box>
              <Box sx={flyoutLabelSx}>{t("summaryTemplate:outputFormat")}</Box>
              <Box
                component="select"
                value={formOutputFormat}
                onChange={(e: React.ChangeEvent<HTMLSelectElement>) =>
                  setFormOutputFormat(e.target.value as OutputFormat)
                }
                sx={flyoutSelectSx}
              >
                <option value="BulletPoints">{t("summaryTemplate:bulletPoints")}</option>
                <option value="NumberedList">{t("summaryTemplate:numberedList")}</option>
                <option value="Prose">{t("summaryTemplate:prose")}</option>
              </Box>
            </Box>

            {/* Length Guidance */}
            <Box>
              <Box sx={flyoutLabelSx}>{t("summaryTemplate:length")}</Box>
              <Box
                component="select"
                value={formLengthGuidance}
                onChange={(e: React.ChangeEvent<HTMLSelectElement>) =>
                  setFormLengthGuidance(e.target.value as LengthGuidance)
                }
                sx={flyoutSelectSx}
              >
                <option value="Brief">{t("summaryTemplate:brief")}</option>
                <option value="Standard">{t("summaryTemplate:standard")}</option>
                <option value="Detailed">{t("summaryTemplate:detailed")}</option>
              </Box>
            </Box>

            {/* Global Instructions */}
            <Box>
              <Box sx={flyoutLabelSx}>{t("summaryTemplate:globalInstructions")}</Box>
              <Box
                component="textarea"
                placeholder={t("summaryTemplate:globalInstructionsPlaceholder")}
                value={formGlobalInstructions}
                onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) =>
                  setFormGlobalInstructions(e.target.value)
                }
                sx={flyoutTextareaSx}
              />
            </Box>

            {/* Sections */}
            <Box>
              <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: "8px" }}>
                <Box sx={flyoutLabelSx}>{t("summaryTemplate:sections")}</Box>
                <Box sx={{ fontSize: "0.72rem", color: "var(--text-tertiary)" }}>
                  {formSections.length}/{MAX_SECTIONS}
                </Box>
              </Box>

              {formSections.map((section, index) => (
                <Box
                  key={section.uid}
                  sx={{
                    mb: "12px",
                    p: "12px",
                    borderRadius: "8px",
                    border: "1px solid var(--border-mid)",
                    backgroundColor: "var(--bg-raised)",
                    display: "flex",
                    flexDirection: "column",
                    gap: "8px",
                  }}
                >
                  <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                    <Box sx={{ fontSize: "0.72rem", fontWeight: 600, color: "var(--text-tertiary)", textTransform: "uppercase", letterSpacing: "0.04em" }}>
                      Section {index + 1}
                    </Box>
                    <Box
                      component="button"
                      onClick={() => removeSection(index)}
                      sx={{
                        display: "inline-flex",
                        alignItems: "center",
                        px: "6px",
                        py: "2px",
                        borderRadius: "4px",
                        border: "1px solid rgba(251,113,133,0.3)",
                        bgcolor: "rgba(251,113,133,0.06)",
                        color: "var(--signal-rose)",
                        fontSize: "0.7rem",
                        fontWeight: 600,
                        fontFamily: "inherit",
                        cursor: "pointer",
                        "&:hover": { bgcolor: "rgba(251,113,133,0.15)" },
                      }}
                    >
                      {t("summaryTemplate:removeSection")}
                    </Box>
                  </Box>
                  <Box
                    component="input"
                    type="text"
                    placeholder={t("summaryTemplate:sectionHeading")}
                    value={section.heading}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                      updateSection(index, "heading", e.target.value)
                    }
                    sx={flyoutInputSx}
                  />
                  <Box
                    component="textarea"
                    placeholder={t("summaryTemplate:sectionInstructionsPlaceholder")}
                    value={section.instructions}
                    onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) =>
                      updateSection(index, "instructions", e.target.value)
                    }
                    sx={flyoutTextareaSx}
                  />
                </Box>
              ))}

              {formSections.length < MAX_SECTIONS && (
                <Box
                  component="button"
                  onClick={addSection}
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: "6px",
                    width: "100%",
                    px: "12px",
                    py: "8px",
                    borderRadius: "8px",
                    border: "1px dashed var(--border-mid)",
                    backgroundColor: "transparent",
                    color: "var(--text-tertiary)",
                    fontSize: "0.8125rem",
                    fontWeight: 600,
                    fontFamily: "inherit",
                    cursor: "pointer",
                    justifyContent: "center",
                    "&:hover": {
                      borderColor: "var(--accent)",
                      color: "var(--accent)",
                      backgroundColor: "var(--accent-dim)",
                    },
                  }}
                >
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} style={{ width: 13, height: 13 }}>
                    <line x1="12" y1="5" x2="12" y2="19" />
                    <line x1="5" y1="12" x2="19" y2="12" />
                  </svg>
                  {t("summaryTemplate:addSection")}
                </Box>
              )}
            </Box>

            {/* Delete confirmation (edit mode only) */}
            {flyoutMode === "edit" && showDeleteConfirm && (
              <DeleteConfirmation
                message={`Are you sure you want to ${t("summaryTemplate:deleteConfirm")}?`}
                error={deleteError}
                onCancel={() => {
                  setShowDeleteConfirm(false);
                  setDeleteError("");
                  deleteMutation.reset();
                }}
                cancelLabel={t("common:cancel")}
                onConfirm={handleDelete}
                confirmLabel={t("summaryTemplate:delete")}
                isPending={deleteMutation.isPending}
                pendingLabel={t("common:deleting")}
              />
            )}
        </FlyoutDrawer>
      }
    >
      {tableContent}
    </CrudPageLayout>
  );
}
